import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { useErrorMessage } from '@/lib/error-message';

/**
 * ErrorState — the standard way to render anything thrown by the API client
 * (05 §6.3): the `ErrorCode` becomes an Arabic sentence, and there is a retry.
 *
 * `RATE_LIMITED` additionally shows the `Retry-After` window, because "try again
 * later" without a number is the least useful sentence in an error message.
 */
export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  /** Overrides the heading (defaults to `errors.title`). */
  title?: string;
  className?: string;
  'data-testid'?: string;
}

export function ErrorState({
  error,
  onRetry,
  title,
  className,
  'data-testid': testId = 'error-state',
}: ErrorStateProps) {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();

  const retryAfter = error instanceof ApiError ? error.retryAfter : undefined;

  return (
    <Alert
      variant="destructive"
      title={title ?? t('errors.title')}
      className={className}
      data-testid={testId}
      action={
        onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        ) : null
      }
    >
      <p>{toMessage(error)}</p>
      {retryAfter !== undefined ? (
        <p className="mt-1 text-xs tabular">{t('common.retryAfter', { seconds: retryAfter })}</p>
      ) : null}
    </Alert>
  );
}
